import React from "react";
import { Link } from "react-router-dom";
import Window from "../components/Window";
import { IconTile } from "../components/Icon";

// Support lines, chosen for being run by and for our communities. Hours
// change often, so we link to each one instead of listing them.
// Numbers checked October 2026.
type Line = { name: string; who: string; phone?: string; text?: string; url: string };

const US: Line[] = [
  {
    name: "Trans Lifeline",
    who: "Peer support run by and for trans people. Open set hours; check their site.",
    phone: "877-565-8860",
    url: "https://translifeline.org",
  },
  {
    name: "BlackLine",
    who: "Crisis support centering Black, Brown, Indigenous, and LGBTQ+ callers. Call or text.",
    phone: "1-800-604-5841",
    url: "https://www.callblackline.com",
  },
  {
    name: "StrongHearts Native Helpline",
    who: "For Native Americans and Alaska Natives facing domestic or sexual violence.",
    phone: "1-844-762-8483 (1-844-7NATIVE)",
    url: "https://strongheartshelpline.org",
  },
  {
    name: "LGBT National Hotline",
    who: "Peer support and local resources for LGBTQ+ people of all ages.",
    phone: "1-888-843-4564",
    url: "https://www.lgbthotline.org",
  },
  {
    name: "The Trevor Project",
    who: "Crisis support for LGBTQ+ young people up to age 24.",
    phone: "1-866-488-7386",
    url: "https://www.thetrevorproject.org",
  },
  {
    name: "988 Suicide & Crisis Lifeline",
    who: "Open 24/7 for anyone.",
    phone: "988",
    text: "988",
    url: "https://988lifeline.org",
  },
];

const CANADA: Line[] = [
  {
    name: "Trans Lifeline (Canada)",
    who: "Peer support run by and for trans people.",
    phone: "877-330-6366",
    url: "https://translifeline.org",
  },
  {
    name: "Hope for Wellness Help Line",
    who: "Open 24/7 for First Nations, Inuit, and Métis people. Phone or online chat; Cree, Ojibway, and Inuktitut on request.",
    phone: "1-855-242-3310",
    url: "https://www.hopeforwellness.ca",
  },
  {
    name: "9-8-8 Suicide Crisis Helpline",
    who: "Open 24/7 for anyone in Canada.",
    phone: "988",
    text: "988",
    url: "https://988.ca",
  },
];

function Lines({ title, lines }: { title: string; lines: Line[] }) {
  return (
    <section className="profile-box bg-white">
      <div className="profile-box-body">
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        <ul className="mt-2 flex flex-col gap-3 text-sm">
          {lines.map((l) => (
            <li key={l.name}>
              <a href={l.url} target="_blank" rel="noreferrer" className="font-bold">
                {l.name}
              </a>
              {l.phone && (
                <>
                  {" "}
                  · call <a href={`tel:${l.phone.replace(/[^0-9]/g, "").slice(0, 11)}`}>{l.phone}</a>
                </>
              )}
              {l.text && <> · text {l.text}</>}
              <span className="block text-ink-soft">{l.who}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function Care() {
  return (
    <Window title="care.txt" tone="mint" minimizable={false}>
      <h1 className="window-heading flex items-center gap-3">
        <IconTile name="heart" tone="green" size={44} /> Care &amp; resources
      </h1>
      <p className="window-sub">
        For the hard days. You don't have to be in crisis to reach out to
        these folks, and you don't have to carry it alone.
      </p>

      <div className="flex flex-col gap-4">
        <section className="profile-box bg-white">
          <div className="profile-box-body text-sm">
            <h2 className="font-display text-lg font-semibold">If you're in danger right now</h2>
            Call your local emergency number (911 in the US and Canada). If
            calling emergency services doesn't feel safe for you, the lines
            below can talk it through with you first.
          </div>
        </section>

        <Lines title="In the US" lines={US} />
        <Lines title="In Canada" lines={CANADA} />

        <section className="profile-box bg-white">
          <div className="profile-box-body text-sm">
            <h2 className="font-display text-lg font-semibold">Little ways to look after yourself here</h2>
            <ul className="ml-5 mt-1 list-disc">
              <li>It's okay to log off. Your letters will wait for you.</li>
              <li>Set your spoon meter on your profile so pen pals know your energy lately.</li>
              <li>Calm mode in Display settings stops the blinking and motion.</li>
              <li>
                Block or report anyone who makes you feel unsafe. They're never
                told, and a real person reads every report (see{" "}
                <Link to="/guidelines">Safety &amp; Rules</Link>).
              </li>
            </ul>
          </div>
        </section>

        <p className="text-sm text-ink-soft">
          We're a pen pal network, not a crisis service, so we can't answer
          emergencies. Know a line run by and for our communities that should
          be here, especially outside the US and Canada? Email us at{" "}
          <a href="mailto:2slgbtqiapenpal@gmail.com">2slgbtqiapenpal@gmail.com</a>.
        </p>
      </div>
    </Window>
  );
}
